/**
 * Error-message extraction, ported from HexagieApp
 * `src/shared/utils/apiError.ts`.
 *
 * The one rule that matters: the sentence meant for the user is in `reason`,
 * not `message`. `message` is the label for the status ("Not allowed",
 * "Access Denied") and comes back null on most errors. `errorCode` is for
 * branching and must never be displayed.
 *
 * Error envelope:
 *   { status, timestamp, errorCode, reason, message, statusCode, subErrors }
 */

const I18N_KEY = /^[a-z][a-z0-9]*(\.[a-zA-Z0-9_-]+)+$/;

const GENERIC_LABELS = new Set([
  "error",
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "conflict",
  "unprocessable entity",
  "internal server error",
  "service unavailable",
  "gateway timeout",
  "too many requests",
]);

const STATUS_MESSAGES = {
  401: "Your session has expired. Please sign in again.",
  403: "This is not allowed on your account.",
  404: "This is no longer available.",
  409: "This changed while you were away. Please refresh and try again.",
  429: "Too many attempts. Please wait a moment and try again.",
};

const errorBody = (error) => error?.response?.data ?? error?.data ?? null;

/** Rejects values that are noise in a toast: markup, i18n keys, status labels. */
const usable = (value) => {
  if (typeof value !== "string") return false;

  const text = value.trim();

  if (!text) return false;
  if (text.startsWith("<")) return false;
  if (text.length > 300) return false;
  if (I18N_KEY.test(text)) return false;

  return !GENERIC_LABELS.has(text.toLowerCase());
};

const subErrorMessages = (body) =>
  (body.subErrors || [])
    .map((sub) => {
      if (!usable(sub?.message)) return null;
      return sub?.field ? `${sub.field}: ${sub.message}` : sub.message;
    })
    .filter((message) => message != null);

export const getApiErrorStatus = (error) => {
  const body = errorBody(error);

  return (
    error?.response?.status ??
    error?.status ??
    (typeof body === "object" ? body?.statusCode : undefined)
  );
};

/** The stable code to branch on. Never render this. */
export const getApiErrorFieldCode = (error) => {
  const body = errorBody(error);

  return typeof body === "object" ? body?.errorCode ?? null : null;
};

export const getApiErrorReason = (error) => {
  const body = errorBody(error);

  if (typeof body === "string") return usable(body) ? body : null;
  if (!body) return null;

  const candidates = [
    body.reason,
    body.detail,
    ...subErrorMessages(body),
    body.error_description,
    body.message,
    body.error,
    body.title,
  ];

  return candidates.find(usable) ?? null;
};

export const getApiErrorMessage = (error, fallback = "Something went wrong") => {
  const reason = getApiErrorReason(error);
  if (reason) return reason;

  const status = getApiErrorStatus(error);
  if (status != null && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];

  return usable(error?.message) ? error.message : fallback;
};
